import {
  type BaseEquitiesSector,
  type BaseFixedIncomeCategory,
  type CashEquivalentCategory,
  type EquitiesSector,
  type EquitiesSectorEnum,
  type FixedIncomeCategory,
  type FixedIncomeCategoryEnum,
  type InvestmentAccountDetail,
  type InvestmentAssetAllocation,
  type InvestmentCashEquivalentCategory,
  type InvestmentEquitiesSector,
  type InvestmentEquitiesSectorBreakdown,
  type InvestmentEquitiesSectorsRecommendationOutput,
  type InvestmentFixedIncomeCategoriesRecommendationOutput,
  type InvestmentFixedIncomeCategory,
  type InvestmentFixedIncomeCategoryBreakdown,
  type InvestmentHolding,
  type Money,
  type PersonalPortfolioOverview,
  type RecommendationPercentageRange,
  type UpdateInvestmentEquitiesSectorsInput,
  type UpdateInvestmentFixedIncomeCategoriesInput,
} from '@egw/api'
import { BaseEquitiesInit, BaseFixedIncomeInit } from '../constants/data/equities'

export const findEquitiesSector = (
  data: InvestmentAccountDetail | PersonalPortfolioOverview | InvestmentAssetAllocation,
  category: EquitiesSector,
): InvestmentEquitiesSector | undefined => {
  // Handle InvestmentAssetAllocation directly (no nested assetAllocation)
  if (data.__typename === 'InvestmentAssetAllocation') {
    return (data as InvestmentAssetAllocation).equities.sectors.find(
      (sector: InvestmentEquitiesSector) => sector.sector === category,
    )
  }

  // Handle nested assetAllocation for other types
  if (!('assetAllocation' in data)) return undefined
  return data.assetAllocation.equities.sectors.find((sector: InvestmentEquitiesSector) => sector.sector === category)
}

export const findFixedIncomeCategory = (
  data: InvestmentAccountDetail | PersonalPortfolioOverview | InvestmentAssetAllocation,
  categoryParam: FixedIncomeCategory,
): InvestmentFixedIncomeCategory | undefined => {
  // Handle InvestmentAssetAllocation directly (no nested assetAllocation)
  if (data.__typename === 'InvestmentAssetAllocation') {
    return (data as InvestmentAssetAllocation).fixedIncome.categories.find(
      (category: InvestmentFixedIncomeCategory) => category.category === categoryParam,
    )
  }

  // Handle nested assetAllocation for other types
  if (!('assetAllocation' in data)) return undefined
  return data.assetAllocation.fixedIncome.categories.find(
    (category: InvestmentFixedIncomeCategory) => category.category === categoryParam,
  )
}

export const findCashEquivalentCategory = (
  data: InvestmentAccountDetail | PersonalPortfolioOverview | InvestmentAssetAllocation,
  categoryParam: CashEquivalentCategory,
): InvestmentCashEquivalentCategory | undefined => {
  // Handle InvestmentAssetAllocation directly (no nested assetAllocation)
  if (data.__typename === 'InvestmentAssetAllocation') {
    return (data as InvestmentAssetAllocation).cashEquivalent.categories.find(
      (category: InvestmentCashEquivalentCategory) => category.category === categoryParam,
    )
  }

  // Handle nested assetAllocation for other types
  if (!('assetAllocation' in data)) return undefined
  return data.assetAllocation.cashEquivalent.categories.find(
    (category: InvestmentCashEquivalentCategory) => category.category === categoryParam,
  )
}

interface HoldingsPageDataInput {
  data: InvestmentAccountDetail | PersonalPortfolioOverview | InvestmentAssetAllocation | undefined
  assetAllocationParam: string
  categoryParam: string
}

interface HoldingsDetailsPageDataInput extends HoldingsPageDataInput {
  symbolParam: string
}

export interface HoldingsPageDataProps {
  accountType: string
  percentage: number
  amount: Money
  category: CashEquivalentCategory | FixedIncomeCategory | EquitiesSector
  holdings: InvestmentHolding[]
}

export const findInvestmentHolding = ({
  category,
  symbolParam,
}: {
  category: InvestmentFixedIncomeCategory | InvestmentEquitiesSector | InvestmentCashEquivalentCategory
  symbolParam: string
}): InvestmentHolding | undefined => {
  return category.holdings.find((holding: InvestmentHolding) => holding.symbol === symbolParam)
}

export const buildHoldingsPageData = ({
  data,
  assetAllocationParam,
  categoryParam,
}: HoldingsPageDataInput): HoldingsPageDataProps | undefined => {
  if (!data) {
    return undefined
  }
  const accountType = data.__typename

  if (assetAllocationParam === 'InvestmentEquitiesAsset') {
    const foundSector = findEquitiesSector(data, categoryParam as EquitiesSector)

    return foundSector
      ? {
          accountType,
          category: foundSector.sector,
          ...foundSector,
        }
      : undefined
  }

  if (assetAllocationParam === 'InvestmentFixedIncomeAsset') {
    const foundCategory = findFixedIncomeCategory(data, categoryParam as FixedIncomeCategory)
    return foundCategory ? { accountType, ...foundCategory } : undefined
  }

  // InvestmentCashEquivalentAsset ELSE
  const foundCategory = findCashEquivalentCategory(data, categoryParam as CashEquivalentCategory)
  return foundCategory ? { accountType, ...foundCategory } : undefined
}

export const extractAllocationHoldingsList = ({
  data,
  assetAllocationParam,
}: {
  assetAllocationParam: string
  data: InvestmentAccountDetail | InvestmentAssetAllocation | undefined
}) => {
  if (data === undefined) {
    return undefined
  }

  let holdings: InvestmentHolding[] = []
  let amount: Money = { currencyCode: 'USD', amount: 0 }

  // Handle InvestmentAssetAllocation directly (no nested assetAllocation)
  if (data.__typename === 'InvestmentAssetAllocation') {
    const assetAllocation = data as InvestmentAssetAllocation

    if (assetAllocationParam === 'InvestmentEquitiesAsset') {
      holdings = assetAllocation.equities.holdings
      amount = assetAllocation.equities.amount
    } else if (assetAllocationParam === 'InvestmentFixedIncomeAsset') {
      holdings = assetAllocation.fixedIncome.holdings
      amount = assetAllocation.fixedIncome.amount
    } else if (assetAllocationParam === 'InvestmentCashEquivalentAsset') {
      holdings = assetAllocation.cashEquivalent.holdings
      amount = assetAllocation.cashEquivalent.amount
    } else if (assetAllocationParam === 'InvestmentOtherAsset') {
      holdings = assetAllocation.other.holdings
      amount = assetAllocation.other.amount
    }
  } else {
    // Handle nested assetAllocation for other types
    if (!('assetAllocation' in data)) return undefined

    if (assetAllocationParam === 'InvestmentEquitiesAsset') {
      holdings = data.assetAllocation.equities.holdings
      amount = data.assetAllocation.equities.amount
    } else if (assetAllocationParam === 'InvestmentFixedIncomeAsset') {
      holdings = data.assetAllocation.fixedIncome.holdings
      amount = data.assetAllocation.fixedIncome.amount
    } else if (assetAllocationParam === 'InvestmentCashEquivalentAsset') {
      holdings = data.assetAllocation.cashEquivalent.holdings
      amount = data.assetAllocation.cashEquivalent.amount
    } else if (assetAllocationParam === 'InvestmentOtherAsset') {
      holdings = data.assetAllocation.other.holdings
      amount = data.assetAllocation.other.amount
    }
  }

  return {
    holdings: holdings.sort((a, b) => b.amount.amount - a.amount.amount),
    amount,
  }
}

export const extractAllocationHoldingsDetails = ({
  data,
  assetAllocationParam,
  symbolParam,
}: {
  assetAllocationParam: string
  data: InvestmentAccountDetail | InvestmentAssetAllocation | undefined
  symbolParam: string
}): InvestmentHolding | undefined => {
  const holdingsList = extractAllocationHoldingsList({ data, assetAllocationParam })

  return holdingsList?.holdings.find((holding: InvestmentHolding) => holding.symbol === symbolParam)
}

export const extractHoldingDetail = ({
  data,
  assetAllocationParam,
  categoryParam,
  symbolParam,
}: HoldingsDetailsPageDataInput): InvestmentHolding | undefined => {
  if (!data) {
    return undefined
  }
  if (assetAllocationParam === 'InvestmentEquitiesAsset') {
    const foundSector = findEquitiesSector(data, categoryParam as EquitiesSector)

    return foundSector ? findInvestmentHolding({ category: foundSector, symbolParam }) : undefined
  }

  if (assetAllocationParam === 'InvestmentFixedIncomeAsset') {
    const foundCategory = findFixedIncomeCategory(data, categoryParam as FixedIncomeCategory)
    return foundCategory ? findInvestmentHolding({ category: foundCategory, symbolParam }) : undefined
  }

  // InvestmentCashEquivalentAsset ELSE
  const foundCategory = findCashEquivalentCategory(data, categoryParam as CashEquivalentCategory)
  return foundCategory ? findInvestmentHolding({ category: foundCategory, symbolParam }) : undefined
}

export const buildAdjustEquitiesForm = (
  formDataInput: InvestmentEquitiesSectorsRecommendationOutput,
): BaseEquitiesSector => {
  return Object.keys(formDataInput).reduce((acc: BaseEquitiesSector, key: string) => {
    if (key === 'currentBreakdown') return acc

    const recommendedRange = formDataInput[
      key as keyof InvestmentEquitiesSectorsRecommendationOutput
    ] as RecommendationPercentageRange

    acc[key as EquitiesSectorEnum] = {
      recommendedRange,
      value: formDataInput.currentBreakdown[key as keyof InvestmentEquitiesSectorBreakdown] as number,
      key: key as unknown as EquitiesSectorEnum,
    }
    return acc
  }, BaseEquitiesInit)
}

export const buildAdjustFixedIncomeForm = (
  formDataInput: InvestmentFixedIncomeCategoriesRecommendationOutput,
): BaseFixedIncomeCategory => {
  return Object.keys(formDataInput).reduce((acc: BaseFixedIncomeCategory, key: string) => {
    if (key === 'currentBreakdown') return acc

    const recommendedRange = formDataInput[
      key as keyof InvestmentFixedIncomeCategoriesRecommendationOutput
    ] as RecommendationPercentageRange

    acc[key as FixedIncomeCategoryEnum] = {
      recommendedRange,
      value: formDataInput.currentBreakdown[key as keyof InvestmentFixedIncomeCategoryBreakdown] as number,
      key: key as unknown as FixedIncomeCategoryEnum,
    }
    return acc
  }, BaseFixedIncomeInit)
}

export const buildAdjustEquitiesInput = (data: BaseEquitiesSector): UpdateInvestmentEquitiesSectorsInput => {
  return Object.keys(data).reduce(
    (acc, key) => {
      acc[key as keyof UpdateInvestmentEquitiesSectorsInput] = data[key as EquitiesSectorEnum].value
      return acc
    },
    {
      healthcare: 0,
      consumer: 0,
      financial: 0,
      technology: 0,
      industry: 0,
      energy: 0,
      realEstate: 0,
      media: 0,
    },
  )
}

export const buildAdjustFixedIncomeInput = (
  data: BaseFixedIncomeCategory,
): UpdateInvestmentFixedIncomeCategoriesInput => {
  return Object.keys(data).reduce(
    (acc, key) => {
      acc[key as keyof UpdateInvestmentFixedIncomeCategoriesInput] = data[key as FixedIncomeCategoryEnum].value
      return acc
    },
    {
      governmentBonds: 0,
      highYieldBonds: 0,
      corporateBonds: 0,
      municipalBonds: 0,
      internationalBonds: 0,
    },
  )
}
